import { useState } from 'react'
import { Navigate } from 'react-router-dom'
import { toast } from 'sonner'
import { useAppDispatch } from '@/store/hooks'
import { api } from '@/store/api'
import { useLanguage } from '@/hooks/useLanguage'
import { useMe } from '@/features/auth/useMe'
import { FullScreenLoader } from '@/features/auth/routes/FullScreenLoader'
import { BetaLayout } from '../components/BetaLayout'
import { BetaInfo } from '../components/BetaInfo'
import { BetaSurvey } from '../components/BetaSurvey'
import { BetaThankYou } from '../components/BetaThankYou'
import {
  useGetStudioQueueCountQuery,
  useSubmitStudioApplicationMutation,
  type BetaUseCase,
} from '../betaApi'
import { betaT } from '../locale'

type Step = 'info' | 'survey'

export const StudioBeta = () => {
  const { language } = useLanguage()
  const l = betaT[language]
  const dispatch = useAppDispatch()

  const { data: me, isLoading: isMeLoading } = useMe()
  const { data: queue } = useGetStudioQueueCountQuery(undefined, {
    skip: !me || me.hasSubmittedStudioApplication,
  })
  const [submit, { isLoading: isSubmitting }] = useSubmitStudioApplicationMutation()
  const [step, setStep] = useState<Step>('info')
  const [useCase, setUseCase] = useState<BetaUseCase | null>(null)

  if (isMeLoading || !me) return <FullScreenLoader />
  if (me.isAuthor || me.isAdmin) return <Navigate to="/dashboard" replace />

  const onSubmit = async () => {
    if (!useCase) return
    try {
      await submit({ useCase }).unwrap()
      // 201 → invalidatesTags['Me'] → me перечитан → ниже рендерится BetaThankYou
    } catch (err) {
      const status = (err as { status?: number }).status
      if (status === 409) {
        dispatch(api.util.invalidateTags(['Me']))
        toast.info(l.applicationAlreadySubmitted)
      } else {
        toast.error(l.submitFailed)
      }
    }
  }

  return (
    <BetaLayout>
      {me.hasSubmittedStudioApplication ? (
        <BetaThankYou />
      ) : step === 'info' ? (
        <BetaInfo queueCount={queue?.count} onStart={() => setStep('survey')} />
      ) : (
        <BetaSurvey
          useCase={useCase}
          setUseCase={setUseCase}
          isSubmitting={isSubmitting}
          onBack={() => setStep('info')}
          onSubmit={onSubmit}
        />
      )}
    </BetaLayout>
  )
}
